import { motion } from 'framer-motion';
import { ChevronRight, Pencil, Star, Calendar, Clock, Send } from 'lucide-react';

function Stepper({ active }) {
  const steps = ['Basics', 'Build', 'Configure', 'Review'];
  return (
    <div className="flex items-center justify-center gap-0 py-5 px-6 bg-[#FAFAFA] rounded-2xl mb-4">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>{i < active ? '✓' : <span className="w-1.5 h-1.5 rounded-full bg-current" />}</div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-16px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

const questions = [
  { q: 'What is your favourite fruit?', type: 'Radio', opts: ['Apple', 'Banana', 'Cantaloupe', 'Dried Fruits'] },
  { q: 'Where have you traveled to in the last 6 months?', type: 'Checkbox', opts: ['London', 'Paris', 'Tokyo', 'Munich'] },
  { q: 'Please rate the quality of service provided', type: 'Rating' },
  { q: 'What are your favourite things to do after work?', type: 'Text' },
  { q: 'When would you like to attend?', type: 'DateTime' },
];

export default function SurveyReviewScene() {
  return (
    <div data-testid="scene-survey-review" className="flex flex-col items-center justify-start h-full px-8 pt-3 overflow-y-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-3xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="text-[12px] font-body text-[#737373] mb-2">
          Home <ChevronRight size={12} className="inline" /> Survey <ChevronRight size={12} className="inline" /> <span className="font-medium text-[#0A0A0A]">Create new survey</span>
        </motion.div>
        <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mb-3">Create New Survey</h2>

        <Stepper active={3} />

        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-body text-[#0A0A0A]">&lt; Back</span>
          <span className="px-3 py-1 rounded border border-[#E5E7EB] text-[12px] font-body text-[#0A0A0A]">Save</span>
        </div>

        {/* Survey info */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-[#E5E7EB] p-4 mb-4 grid grid-cols-2 gap-4">
          <div><p className="text-[10px] font-body text-[#737373]">Survey title</p><p className="font-heading font-bold text-sm text-[#0A0A0A]">Q4 Patient Satisfaction Survey</p></div>
          <div><p className="text-[10px] font-body text-[#737373]">Total questions</p><p className="font-heading font-bold text-sm text-[#0A0A0A]">3</p></div>
        </motion.div>

        {/* Survey questions review */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Survey questions</h3>
            <Pencil size={14} className="text-[#737373]" />
          </div>
          <p className="font-body text-xs text-[#737373] mb-3">See how your survey will look for your audience</p>
          <div className="bg-[#FAFAFA] rounded-xl border border-[#E5E7EB] p-4 space-y-4">
            {questions.map((q, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + i * 0.12 }}>
                <p className="font-body font-bold text-[12px] text-[#0A0A0A]">Q{i + 1}  {q.q}<span className="text-red-500">*</span></p>
                {q.opts && <div className="mt-1 ml-4 space-y-0.5">{q.opts.map(o => <p key={o} className="text-[11px] font-body text-[#737373] flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded${q.type === 'Checkbox' ? '' : '-full'} border border-[#737373] inline-block`} />{o}</p>)}</div>}
                {q.type === 'Rating' && <div className="mt-1 flex items-center gap-0.5"><span className="text-[10px] text-[#737373] mr-1">Poor</span>{[1,2,3,4,5].map(s => <Star key={s} size={12} className="text-[#E5E7EB]" />)}<span className="text-[10px] text-[#737373] ml-1">Excellent</span></div>}
                {q.type === 'Text' && <div className="mt-1 px-2 py-1.5 rounded border border-[#E5E7EB] text-[11px] font-body text-[#737373]">Swimming, cooking, etc</div>}
                {q.type === 'DateTime' && <div className="mt-1 grid grid-cols-2 gap-2"><div className="flex items-center gap-1 px-2 py-1.5 rounded border border-[#E5E7EB] text-[11px] font-body text-[#737373]"><Calendar size={10} />Date</div><div className="flex items-center gap-1 px-2 py-1.5 rounded border border-[#E5E7EB] text-[11px] font-body text-[#737373]"><Clock size={10} />Time</div></div>}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Audience review */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
          className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Audience details</h3>
            <Pencil size={14} className="text-[#737373]" />
          </div>
          <p className="text-[11px] font-body text-[#737373] mb-2">User group</p>
          <div className="flex gap-2 mb-2">
            {['Insurance Companies', 'TPAs', 'Providers'].map(t => (
              <span key={t} className="px-2 py-0.5 rounded bg-teal-600/10 text-teal-700 text-[10px] font-body font-medium">{t}</span>
            ))}
          </div>
          <p className="text-[11px] font-body text-[#737373]">Total recipients</p>
          <p className="font-heading font-bold text-lg text-[#0A0A0A]">278</p>
        </motion.div>

        {/* Publishing review */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}
          className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base text-[#0A0A0A]">Publishing schedule</h3>
            <Pencil size={14} className="text-[#737373]" />
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div><p className="text-[10px] font-body text-[#737373]">Start date</p><p className="font-body font-bold text-sm text-[#0A0A0A]">25 Dec' 25</p></div>
            <div><p className="text-[10px] font-body text-[#737373]">End date</p><p className="font-body font-bold text-sm text-[#0A0A0A]">25 Dec' 29</p></div>
            <div><p className="text-[10px] font-body text-[#737373]">Start time</p><p className="font-body font-bold text-sm text-[#0A0A0A]">10:00 AM</p></div>
            <div><p className="text-[10px] font-body text-[#737373]">End time</p><p className="font-body font-bold text-sm text-[#0A0A0A]">10:00 AM</p></div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="flex justify-center mb-4">
          <button className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm">
            <Send size={14} /> Submit for approval
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
